import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Save, X, Package, Tag, ToggleLeft, ToggleRight } from 'lucide-react';

interface Service {
  id: string;
  user_id: string;
  name: string;
  description: string;
  base_price: number;
  duration_minutes: number;
  is_active: boolean;
  created_at: string;
}

interface InventoryItem {
  id: string;
  item_name: string;
  current_stock: number;
  unit: string;
}

interface ServiceInventoryRequirement {
  id: string;
  service_id: string;
  inventory_item_id: string;
  quantity_required: number;
  notes: string;
}

interface ServiceWithRequirements extends Service {
  requirements: ServiceInventoryRequirement[];
}

export function ServicesManager() {
  const [services, setServices] = useState<ServiceWithRequirements[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Form state for new/edit service
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    base_price: 0,
    duration_minutes: 60,
  });

  useEffect(() => {
    fetchServices();
    fetchInventory();
  }, []);

  const fetchServices = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('user_id', user.id)
        .order('name', { ascending: true });

      if (error) throw error;

      // Fetch requirements for each service
      const servicesWithReqs = await Promise.all(
        (data || []).map(async (service) => {
          const { data: reqs } = await supabase
            .from('service_inventory_requirements')
            .select('*')
            .eq('service_id', service.id);

          return {
            ...service,
            requirements: reqs || [],
          };
        })
      );

      setServices(servicesWithReqs);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('id, item_name, current_stock, unit')
        .order('item_name', { ascending: true });

      if (error) throw error;
      setInventoryItems(data || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const handleCreateService = async () => {
    if (!formData.name || formData.base_price <= 0) {
      toast.error('Please enter a service name and valid price');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        return;
      }

      const { error } = await supabase
        .from('services')
        .insert([{
          user_id: user.id,
          name: formData.name,
          description: formData.description,
          base_price: formData.base_price,
          duration_minutes: formData.duration_minutes,
          is_active: true,
        }])
        .select()
        .single();

      if (error) throw error;

      toast.success('Service created!');
      setShowCreateModal(false);
      setFormData({ name: '', description: '', base_price: 0, duration_minutes: 60 });
      fetchServices();
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error('Failed to create service');
    }
  };

  const handleUpdateService = async (id: string) => {
    if (!formData.name || formData.base_price <= 0) {
      toast.error('Please enter a service name and valid price');
      return;
    }

    try {
      const { error } = await supabase
        .from('services')
        .update({
          name: formData.name,
          description: formData.description,
          base_price: formData.base_price,
          duration_minutes: formData.duration_minutes,
        })
        .eq('id', id);

      if (error) throw error;

      toast.success('Service updated!');
      setEditingId(null);
      fetchServices();
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('services')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Service ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchServices();
    } catch (error) {
      console.error('Error toggling service:', error);
      toast.error('Failed to update service');
    }
  };

  const handleDeleteService = async (id: string) => {
    if (!confirm('Are you sure you want to delete this service? This cannot be undone.')) return;

    try {
      // Delete requirements first (cascade should handle this, but be safe)
      await supabase
        .from('service_inventory_requirements')
        .delete()
        .eq('service_id', id);

      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Service deleted');
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleAddRequirement = async (serviceId: string, inventoryItemId: string, quantityRequired: number, notes: string) => {
    if (!inventoryItemId || quantityRequired <= 0) {
      toast.error('Please select an inventory item and valid quantity');
      return;
    }

    try {
      const { error } = await supabase
        .from('service_inventory_requirements')
        .insert([{
          service_id: serviceId,
          inventory_item_id: inventoryItemId,
          quantity_required: quantityRequired,
          notes: notes || null,
        }]);

      if (error) throw error;

      toast.success('Inventory requirement added');
      fetchServices();
    } catch (error) {
      console.error('Error adding requirement:', error);
      toast.error('Failed to add requirement');
    }
  };

  const handleDeleteRequirement = async (requirementId: string) => {
    try {
      const { error } = await supabase
        .from('service_inventory_requirements')
        .delete()
        .eq('id', requirementId);

      if (error) throw error;

      toast.success('Requirement removed');
      fetchServices();
    } catch (error) {
      console.error('Error deleting requirement:', error);
      toast.error('Failed to remove requirement');
    }
  };

  const startEditing = (service: Service) => {
    setEditingId(service.id);
    setFormData({
      name: service.name,
      description: service.description,
      base_price: service.base_price,
      duration_minutes: service.duration_minutes,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setFormData({ name: '', description: '', base_price: 0, duration_minutes: 60 });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-slate-600 font-bold">Loading services...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-black uppercase">Services</h2>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 rounded-2xl font-black uppercase"
        >
          <Plus className="w-4 h-4" />
          Add Service
        </Button>
      </div>

      {services.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-white rounded-[2rem] border-2 border-slate-100">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="font-bold text-sm">No services yet</p>
          <p className="text-xs mt-1">Create services to use in quotes and track inventory requirements</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white border-2 border-slate-100 rounded-[2rem] p-6 shadow-sm hover:border-purple-200 transition-colors"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  {editingId === service.id ? (
                    <div className="space-y-3">
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="h-12 rounded-xl font-bold"
                        placeholder="Service Name"
                      />
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="h-12 rounded-xl"
                        placeholder="Description"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[9px] font-black uppercase text-slate-500">Base Price ($)</label>
                          <Input
                            type="number"
                            value={formData.base_price}
                            onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })}
                            className="h-10 rounded-xl"
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-black uppercase text-slate-500">Duration (min)</label>
                          <Input
                            type="number"
                            value={formData.duration_minutes}
                            onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                            className="h-10 rounded-xl"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-black uppercase text-slate-900">{service.name}</h3>
                        <span className={`text-[8px] font-black px-2 py-0.5 rounded uppercase ${
                          service.is_active
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-slate-50 text-slate-500 border border-slate-200'
                        }`}>
                          {service.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 font-bold">{service.description || 'No description'}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <p className="text-sm font-black text-purple-600">${service.base_price.toFixed(2)}</p>
                        <p className="text-sm font-bold text-slate-400">{service.duration_minutes} min</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex gap-2">
                  {editingId === service.id ? (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleUpdateService(service.id)}
                        className="bg-green-600 hover:bg-green-700 rounded-xl"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelEditing}
                        className="rounded-xl"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditing(service)}
                        className="rounded-xl"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleActive(service.id, service.is_active)}
                        className="rounded-xl"
                      >
                        {service.is_active ? (
                          <ToggleRight className="w-5 h-5 text-green-600" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-slate-400" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteService(service.id)}
                        className="rounded-xl text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Inventory Requirements Section */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-black uppercase text-slate-500 flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    Inventory Requirements
                  </h4>
                  <AddRequirementForm
                    inventoryItems={inventoryItems}
                    onAdd={(itemId, qty, notes) => handleAddRequirement(service.id, itemId, qty, notes)}
                  />
                </div>

                {service.requirements.length === 0 ? (
                  <p className="text-xs text-slate-400 font-bold italic">No inventory requirements set</p>
                ) : (
                  <div className="space-y-2">
                    {service.requirements.map((req) => {
                      const invItem = inventoryItems.find(i => i.id === req.inventory_item_id);
                      return (
                        <div
                          key={req.id}
                          className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">
                              {invItem?.item_name || 'Unknown Item'}
                            </span>
                            <span className="text-xs font-black text-purple-600">
                              {req.quantity_required} {invItem?.unit || 'units'}
                            </span>
                            {invItem && invItem.current_stock < req.quantity_required && (
                              <span className="text-[9px] font-black bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                LOW STOCK
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteRequirement(req.id)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Service Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full">
            <h3 className="text-xl font-black text-slate-900 uppercase mb-4">Create New Service</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Service Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 rounded-xl"
                  placeholder="e.g. Full Detail Package"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="h-12 rounded-xl"
                  placeholder="Brief description of the service"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Base Price ($)</label>
                  <Input
                    type="number"
                    value={formData.base_price}
                    onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })}
                    className="h-12 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Duration (min)</label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                    className="h-12 rounded-xl"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleCreateService}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-2xl font-black uppercase"
                >
                  Create Service
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', description: '', base_price: 0, duration_minutes: 60 });
                  }}
                  className="rounded-2xl"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-component: Add Requirement Form
function AddRequirementForm({
  inventoryItems,
  onAdd,
}: {
  inventoryItems: InventoryItem[];
  onAdd: (itemId: string, quantity: number, notes: string) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');

  const handleAdd = () => {
    if (selectedItemId && quantity > 0) {
      onAdd(selectedItemId, quantity, notes);
      setSelectedItemId('');
      setQuantity(1);
      setNotes('');
      setShowForm(false);
    }
  };

  if (!showForm) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowForm(true)}
        className="rounded-xl text-xs font-black uppercase"
      >
        <Plus className="w-3 h-3 mr-1" />
        Add Item
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <select
        value={selectedItemId}
        onChange={(e) => setSelectedItemId(e.target.value)}
        className="h-8 rounded-lg border border-slate-200 text-xs font-bold px-2"
      >
        <option value="">Select item...</option>
        {inventoryItems.map((item) => (
          <option key={item.id} value={item.id}>
            {item.item_name} ({item.current_stock} {item.unit})
          </option>
        ))}
      </select>
      <Input
        type="number"
        value={quantity}
        onChange={(e) => setQuantity(parseFloat(e.target.value) || 1)}
        className="h-8 w-20 rounded-lg border border-slate-200 text-xs font-bold"
        placeholder="Qty"
      />
      <Button
        size="sm"
        onClick={handleAdd}
        className="h-8 rounded-lg bg-green-600 hover:bg-green-700"
      >
        <Save className="w-3 h-3" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowForm(false)}
        className="h-8 rounded-lg"
      >
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
}
